import { useState, useEffect } from 'react';
import { fetchInterviewQuestions, sendInterviewConclusion, fetchTextToSpeech, fetchCandidateData, storeInterviewData } from '../../../../api/chat';
import { formatInterviewData } from '../../utils';
import { buildInterviewGreeting } from '../../../../utils/greeting';

const coach = {
  name: 'Rê',
  avatar: '/images/coach-profile.png',
};

const useConversation = (talent, talent_uuid, app_id, candidate, vacancyTitle, companyName, interviewStarted) => {
  const [conversation, setConversation] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [loadingInterview, setLoadingInterview] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [inputDisabled, setInputDisabled] = useState(true);
  const [finalMessage, setFinalMessage] = useState(false);
  const [interview, setInterview] = useState(null);
  const [interviewFinished, setInterviewFinished] = useState(false); // Novo estado
  const [loadingFeedback, setLoadingFeedback] = useState(false); // Novo estado
  const [interviewStored, setInterviewStored] = useState(null); // Novo estado

  useEffect(() => {
    if (interviewStarted && candidate) {
      if (interview) {
        const initialConversation = [];
        interview.interview.forEach((item, index) => {
          initialConversation.push(
            {
              type: 'coach',
              text: item[`question_${index + 1}`].question,
              user: coach,
            },
            {
              type: 'candidate',
              text: item[`question_${index + 1}`].answer,
              user: candidate,
            }
          );
        });

        setConversation([
          {
            type: 'coach',
            text: buildInterviewGreeting(candidate?.name.split(' ')[0], vacancyTitle, companyName),
            user: coach,
          },
          {
            type: 'coach',
            text: `Podemos iniciar nosso papo? Você poderá responder minhas perguntas por texto ou áudio, ok!?`,
            user: coach,
          },
          ...initialConversation,
          {
            type: 'coach',
            text: interview.conclusion,
            user: coach,
          },
          {
            type: 'coach',
            text: `Sua nota foi: ${interview.score}/100`,
            user: coach,
          },
          {
            type: 'coach',
            text: `Gostou da experiência? Compartilhe em suas redes sociais e marque a @recruteibr! 😊`,
            user: coach,
          },
        ]);
        setInputDisabled(true);
        setInterviewFinished(true); // Entrevista finalizada
      } else {
        const getInterviewQuestions = async () => {
          setLoadingInterview(true);
          try {
            const data = await fetchInterviewQuestions(app_id, vacancyTitle);
            setQuestions(data.questions || []);
            setConversation([
              {
                type: 'coach',
                text: buildInterviewGreeting(candidate?.name.split(' ')[0], vacancyTitle, companyName),
                user: coach,
              },
              {
                type: 'coach',
                text: `Podemos iniciar nosso papo? Você poderá responder minhas perguntas por texto ou áudio, ok!?`,
                user: coach,
              },
            ]);
          } catch (error) {
            console.error('Error fetching questions:', error);
          } finally {
            setInputDisabled(false);
            setLoadingInterview(false);
          }
        };

        getInterviewQuestions();
      }
    }
  }, [interviewStarted, candidate, vacancyTitle, companyName, interview]);

  const handleSendMessage = (message, audioUrl = null) => {
    const newConversation = [...conversation, { type: 'candidate', text: message, audioUrl: audioUrl, user: candidate }];
    setConversation(newConversation);

    if (questionIndex >= 1 && questionIndex <= questions.length) {
      setAnswers((prevAnswers) => [
        ...prevAnswers,
        {
          question: questions[questionIndex - 1],
          answer: message,
          audioUrl: audioUrl
        },
      ]);
    }

    if (questions.length > 0 && questionIndex < questions.length) {
      setTimeout(async () => {
        const question = questions[questionIndex];
        // Add loading message
        const loadingMessage = { type: 'coach', text: '...', loading: true, user: coach };
        setConversation((prevConversation) => [
          ...prevConversation,
          loadingMessage,
        ]);

        try {
          const response = await fetchTextToSpeech(question);
          const blob = new Blob([response], {type: 'audio/mpeg'});
          const audioUrl = URL.createObjectURL(blob);

          // Replace loading message with actual message
          setConversation((prevConversation) => [
            ...prevConversation.slice(0, -1),
            { type: 'coach', text: question, audioUrl, user: coach },
          ]);
          setQuestionIndex(questionIndex + 1);
        } catch (error) {
          console.error('Error fetching audio:', error);
        }
      }, 1000);
    } else if (questions.length > 0) {
      setAnswers((prevAnswers) => [
        ...prevAnswers,
        {
          question: questions[questionIndex - 1],
          answer: message,
          audioUrl: audioUrl
        },
      ]);

      setTimeout(async () => {
        setFinalMessage(true);
        setConversation((prevConversation) => [
          ...prevConversation,
          { type: 'coach', text: 'Muito obrigada, adorei nosso papo!', user: coach },
        ]);

        setInputDisabled(true);
        setLoadingFeedback(true); // Ativar loading do feedback

        const finalAnswers = [
          ...answers,
          {
            question: questions[questionIndex - 1],
            answer: message,
            audioUrl: audioUrl
          },
        ];

        const interviewData = formatInterviewData(finalAnswers, questions);

        const getInterviewConclusion = async () => {
          try {
            const data = await sendInterviewConclusion(interviewData, vacancyTitle);
            const conclusionResponse = await fetchTextToSpeech(data.conclusion);
            const conclusionAudioUrl = URL.createObjectURL(conclusionResponse);

            setTimeout(async () => {
              setConversation((prevConversation) => [
                ...prevConversation,
                { type: 'coach', text: data.conclusion, audioUrl: conclusionAudioUrl, user: coach },
                { type: 'coach', text: `Sua nota foi: ${data.score}/100`, user: coach },
              ]);
              setLoadingFeedback(false); // Desativar loading do feedback
              setInterviewFinished(true); // Entrevista finalizada
              setInterviewStored(data); // Guardar dados da entrevista armazenada

              // Save interview data
              const storedData = await storeInterviewData(talent_uuid, {
                application_id: Number(app_id),
                vacancy_id: talent?.application?.vacancy_id,
                company_id: talent?.application?.company_id,
                vacancy_title: vacancyTitle,
                company_name: companyName,
                status: 'finished',
                interview: interviewData['interview'],
                conclusion: data?.conclusion,
                score: data?.score,
              });

              setInterviewStored(storedData); // Guardar dados da entrevista armazenada

            }, 2000);
          } catch (error) {
            console.error('Error fetching conclusion:', error);
            setLoadingFeedback(false); // Desativar loading do feedback em caso de erro
          }
        };

        getInterviewConclusion();
      }, 1000);
    }
  };

  return {
    conversation,
    handleSendMessage,
    inputDisabled,
    finalMessage,
    loadingInterview,
    interview,
    setInterview,
    interviewFinished, // Retornar o novo estado
    loadingFeedback, // Retornar o novo estado
    interviewStored, // Retornar o novo estado
  };
};

export default useConversation;
